import type { ReactNode } from 'react'

import { styled } from 'styled-components'

import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
  parseOptionalISODate,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import {
  AuctionState,
  BuyNowState,
} from '@/buyer-portal/components/features/listing/actions/auction/const'
import type { AuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import { calculateFeePercent } from '@/buyer-portal/components/features/listing/hooks/calculateFeePercent'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useSuppressShippingLine } from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import { useTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import { getParcelZeroQuoteCode } from '@/buyer-portal/components/ParcelRateList/detectNoRates'
import { useDisplayPhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import {
  TlcBreakdown,
  TlcBreakdownSignInNotice,
} from '@/components/common/TlcBreakdown'
import { TlcStats } from '@/components/common/TlcStats'
import { Standard } from '@/helpers/css/colors'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { MissingDataBanner } from '../../../MissingDataBanner'
import { BuyNowStatus } from '../../Bid/Tag'
import { ContractInfoBanner } from '../../ContractInfoBanner'
import { ActionCardFooter } from '../ActionCardFooter'
import { StatusBanner } from '../StatusBanner'
import { ActionCard, ActionCardBody } from '../ui'
import { BidActions } from './BidActions'
import { BidHeader } from './BidHeader'
import { BuyNowActions } from './BuyNowActions'

const ns = 'Listing.actionColumn.actionCard.bid'

const PopcornNote = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const PopcornLink = styled.a`
  color: ${Colors.Semantic.Neutral.dark_grey};
  text-decoration: underline;
`

type BidActionCardProps = {
  listing: OrchestratedAuctionListingDetails
  auctionDetailState: AuctionDetailState
  isVisibilityEnded: boolean
  order?: OrderResponseDto | null
  topBanner?: ReactNode
}

export const BidActionCard = (props: BidActionCardProps) => {
  const { listing } = props

  if (!listing.auction._id) {
    return (
      <MissingDataBanner
        titleId="Auction.details.actionColumn.missingData.title"
        messageId="Auction.details.actionColumn.missingData.auction"
        log={{
          level: 'error',
          message: 'BidActionCard: listing missing auction id',
          metadata: {
            listingId: listing._id,
            missingField: 'auction._id',
          },
        }}
      />
    )
  }

  return <BidActionCardWithAuctionId {...props} />
}

const BidActionCardWithAuctionId = ({
  auctionDetailState,
  listing,
  isVisibilityEnded: _isVisibilityEnded,
  order,
  topBanner,
}: BidActionCardProps) => {
  const { auction } = listing

  const isLoggedIn = useIsLoggedIn()

  const {
    details,
    buyNowActions: { quickBuy },
    editDeliveryMethod,
    selectedQuote,
    shippingQuotePending,
  } = useBidFormContext()
  const {
    tagStatus,
    userBidAmount,
    isEnded,
    isPaused,
    bidDeadline,
    additionalCharges,
    msrpPrice,
    units,
    currency,
  } = details

  const { trackButtonClicked } = useAnalytics()
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const {
    fdActive,
    fdHasBuyNow,
    earlyPrice: fdEarlyPrice,
  } = useFirstDibsPurchase(listing)
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  const sharedAnalyticsProps = {
    screenName: 'listing_detail_page' as const,
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    is_new_bid_box_design: true,
    is_total_landed_cost_displayed: isTlcDisplayed,
    ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
  }

  const { basis, bstockFee, isWithoutShipping } = additionalCharges
  const suppressShippingLine = useSuppressShippingLine(listing, selectedQuote)
  const hasNoParcelRate = getParcelZeroQuoteCode(selectedQuote) !== null
  const feePercent = calculateFeePercent(bstockFee, basis)

  // Derived here rather than read off `details`: max tax exposure is only known
  // below the bid form provider, and the displayed figures include it.
  const {
    shippingCents,
    tlcPercentOfMsrpWithTax,
    tlcPerUnitWithTax,
    tlcTaxInfo,
    tlcVatInfo,
    totalLandedCostWithTax,
  } = useTlcTotals({
    additionalCharges,
    currency,
    listingId: listing._id,
    listingType: listing.listingType,
    msrpPrice,
    selectedQuote,
    units,
  })

  const auctionWonAsBuyNow = !!auction.bid?.buyNowBid
  // Selects the card variant, First Dibs included.
  const displayPhase = useDisplayPhase(listing)
  const isBinoDisplay = displayPhase === 'BINO'
  // During FD window: auction is Scheduled (hasn't started yet) but FD allows
  // early bidding, so force auctionState to Active. Also force buyNowState to
  // Active when First Dibs offers Buy Now so BuyNowActions doesn't hide.
  const effectiveAuctionDetailState: AuctionDetailState = fdActive
    ? {
        ...auctionDetailState,
        auctionState: AuctionState.Active,
        ...(fdHasBuyNow ? { buyNowState: BuyNowState.Active } : {}),
      }
    : auctionDetailState
  const isPopcorn = !(auction.initialEndTime === auction.actualEndTime)

  const buyNowStatus =
    auctionWonAsBuyNow && tagStatus === BidStatus.WON
      ? BuyNowStatus.BOUGHT
      : auctionWonAsBuyNow && tagStatus === BidStatus.LOST
        ? BuyNowStatus.SOLD
        : BidStatus.NO_BID

  const endDate = parseOptionalISODate(bidDeadline)

  // A buy-now purchase transiently registers as a WINNING bid until the
  // auction-close event lands — that isn't a bidding contest, so don't show
  // the winning banner for it.
  const isWinning = tagStatus === BidStatus.WINNING && !auctionWonAsBuyNow
  const isLosing = tagStatus === BidStatus.LOSING
  // The winning/losing status bar takes the top slot over the First Dibs strip.
  const showStatusBanner = isWinning || isLosing

  return (
    <ActionCard>
      {!showStatusBanner && topBanner}
      {showStatusBanner && (
        <StatusBanner color={isWinning ? Standard.Green : Standard.Red}>
          <FormattedMessage
            id={`${ns}.statusBanner.${isWinning ? 'winning' : 'losing'}`}
          />
        </StatusBanner>
      )}
      <BidHeader
        displayPhase={displayPhase}
        hasBanner={!!topBanner || showStatusBanner}
        isPopcorn={isPopcorn}
        listing={listing}
        priceOverride={fdEarlyPrice}
      />

      {isLoggedIn && (
        <TlcStats
          currency={currency}
          msrpPrice={msrpPrice}
          tlcPercentOfMsrpWithTax={tlcPercentOfMsrpWithTax}
          tlcPerUnitWithTax={tlcPerUnitWithTax}
          units={units}
          {...(isBinoDisplay && {
            costPerUnitTooltipId: `${ns}.buyNow.tlcStats.costPerUnitTooltip`,
            percentMsrpTooltipId: `${ns}.buyNow.tlcStats.percentMsrpTooltip`,
          })}
        />
      )}

      <ActionCardBody>
        {isBinoDisplay ? (
          <BuyNowActions
            auctionDetailState={effectiveAuctionDetailState}
            buyNowStatus={buyNowStatus}
            editDeliveryMethod={editDeliveryMethod}
            hasNoParcelRate={hasNoParcelRate}
            listing={listing}
            order={order}
            quickBuy={quickBuy}
            shippingQuotePending={shippingQuotePending}
          />
        ) : (
          <BidActions
            auctionDetailState={effectiveAuctionDetailState}
            editDeliveryMethod={editDeliveryMethod}
            isPaused={isPaused || shippingQuotePending}
            listing={listing}
            order={order}
            shippingQuotePending={shippingQuotePending}
            userBidAmount={userBidAmount}
          />
        )}

        {listing.listingType !== 'CONTRACT' &&
          (isLoggedIn ? (
            <TlcBreakdown
              basis={basis}
              bstockFee={bstockFee}
              currency={currency}
              feePercent={feePercent}
              isWithoutShipping={isWithoutShipping}
              isShippingUnavailable={hasNoParcelRate}
              suppressShippingLine={suppressShippingLine}
              onToggle={() =>
                trackButtonClicked({
                  buttonName: 'total_landed_cost_breakdown',
                  ...sharedAnalyticsProps,
                })
              }
              shippingCents={shippingCents}
              taxInfo={tlcTaxInfo}
              totalLandedCostWithTax={totalLandedCostWithTax}
              vatInfo={tlcVatInfo}
              {...(isBinoDisplay && {
                amountLabelId: `${ns}.buyNow.tlcBreakdown.amount`,
                tooltipId: `${ns}.buyNow.tlcBreakdown.tooltip`,
                tooltipWithTaxId: `${ns}.buyNow.tlcBreakdown.tooltipWithTax`,
                tooltipWithVatId: `${ns}.buyNow.tlcBreakdown.tooltipWithVat`,
              })}
            />
          ) : (
            <TlcBreakdownSignInNotice />
          ))}

        {listing.listingType === 'CONTRACT' && <ContractInfoBanner />}
      </ActionCardBody>

      <ActionCardFooter
        endDate={endDate}
        endDateLabelId={`${ns}.footer.endDateLabel`}
        endDateNote={
          displayPhase !== 'BINO' && !isEnded ? (
            <PopcornNote>
              <FormattedMessage
                id={`${ns}.footer.popcornNote`}
                values={{
                  popcornBiddingLink: (chunks: ReactNode) => (
                    <PopcornLink
                      href="https://bstock.com/faq/buyer-faq/bidding/why-does-b-stock-use-proxy-bidding-and-popcorn-bidding/"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        trackButtonClicked({
                          buttonName: 'info_popcorn_bidding',
                          ...sharedAnalyticsProps,
                        })
                      }
                    >
                      {chunks}
                    </PopcornLink>
                  ),
                }}
              />
            </PopcornNote>
          ) : undefined
        }
        onReportProblem={() =>
          trackButtonClicked({
            buttonName: 'report_problem_with_listing',
            ...sharedAnalyticsProps,
          })
        }
        reportProblemLabelId={`${ns}.footer.reportProblem`}
      />
    </ActionCard>
  )
}
